
import { useState, useEffect } from 'react';
import { usePermissions } from '@/contexts/unified-permissions';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';

import { toast } from '@/hooks/use-toast';
import { ReferralStorageService } from '@/services/referral/ReferralStorageService';
import { EmailConfirmationWaiting } from '@/components/auth/EmailConfirmationWaiting';
import { validateSignupForm, getEmailValidationState, validatePasswordStrength } from '@/utils/validation';
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator';
import { classifyAuthError, formatAuthError } from '@/services/auth/errorHandling';
import { generateUniqueUsername, type UsernameGenerationResult } from '@/services/auth/usernameService';
import { UsernameGenerationFeedback } from '@/components/auth/UsernameGenerationFeedback';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isGeneratingUsername, setIsGeneratingUsername] = useState(false);
  const [usernameResult, setUsernameResult] = useState<UsernameGenerationResult | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  
  const {
    signUp,
    permissionsStatus
  } = usePermissions();

  // Use isLoading from permissionsStatus
  const isLoading = permissionsStatus === 'loading';

  // Check for referral code from multiple sources
  useEffect(() => {
    // Priority 1: Check URL query params (highest priority, most explicit)
    const urlRefCode = searchParams.get('ref');
    if (urlRefCode) {

      setReferralCode(urlRefCode);
      
      // Store the code from URL to persist it
      const campaign = searchParams.get('campaign');
      ReferralStorageService.storeReferralCode(urlRefCode, campaign || undefined);
      return;
    }
    
    // Priority 2: Check storage (fallback)
    const storedCode = ReferralStorageService.getStoredReferralCode();
    if (storedCode) {

      setReferralCode(storedCode);
      return;
    }
    

  }, [searchParams]);

  // Email validation handler with real-time feedback
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Clear previous errors
    setEmailError('');
    setError('');

    // Only validate if user has typed something and then moved away from empty
    if (newEmail.trim() !== '') {
      const validation = getEmailValidationState(newEmail);
      if (!validation.isValid && !validation.isEmpty) {
        setEmailError(validation.errorMessage || '');
      }
    }
  };

  // Password validation handler with strength checking
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Clear previous errors
    setPasswordError('');
    setError('');

    // Enhanced password validation with strength feedback
    if (newPassword.trim() !== '') {
      const validation = validatePasswordStrength(newPassword, 8);
      if (!validation.isValid) {
        setPasswordError(validation.errorMessage || 'Password requirements not met');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    setPasswordError('');
    setSignupSuccess(false);

    // Comprehensive form validation using shared utilities
    const validation = validateSignupForm({
      email,
      password,
      fullName: fullName || undefined
    });

    if (!validation.isValid) {
      // Set specific field errors
      if (validation.errors.email) {
        setEmailError(validation.errors.email);
      }
      if (validation.errors.password) {
        setPasswordError(validation.errors.password);
      }
      if (validation.errors.fullName) {
        setError(validation.errors.fullName);
      }

      // Set general error if no specific field errors
      if (!validation.errors.email && !validation.errors.password && !validation.errors.fullName) {
        setError('Please check your input and try again');
      }
      return;
    }
    
    try {
      setIsGeneratingUsername(true);

      // Generate unique username with collision prevention
      const usernameGenerationResult = await generateUniqueUsername(email);
      setUsernameResult(usernameGenerationResult);

      // Log username generation for debugging
      if (!usernameGenerationResult.isOriginal) {
        console.log(`Username collision detected. Generated alternative: ${usernameGenerationResult.username}`);
      }

      // Include referral code and newsletter consent in user metadata
      const userData = {
        username: usernameGenerationResult.username,
        full_name: fullName,
        newsletter_consent: marketingConsent,
        ...(referralCode && { referral_code: referralCode })
      };
      
      // Log signup attempt with referral info for debugging

      
      await signUp(email, password, userData);
      
      // Show success message after signup
      setSignupSuccess(true);
      
      // Show referral success message if applicable
      if (referralCode) {
        toast({
          title: "Referral applied!",
          description: "Your 30% discount will be applied to your subscription.",
          variant: "default"
        });
      }

      // Newsletter subscription will happen automatically when user confirms their email
      // No need to subscribe here - it's stored in user metadata
    } catch (error: unknown) {
      console.error('Signup error:', error);

      // Use enhanced error classification
      const classification = classifyAuthError(error);
      const formattedError = formatAuthError(classification);

      // Set error message with specific guidance
      setError(`${formattedError.title}. ${formattedError.description}`);
    } finally {
      setIsGeneratingUsername(false);
    }
  };

  return <div className="flex min-h-screen flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-primary">The Mat Monster</h1>
          <h2 className="mt-6 text-3xl font-bold tracking-tight">
            {signupSuccess ? "Check your email" : "Create your account"}
          </h2>
          {!signupSuccess && (
            <p className="mt-2 text-sm text-muted-foreground">
              Or{" "}
              <Link to="/auth/login" className="font-medium text-primary hover:text-primary/90">
                sign in to your account
              </Link>
            </p>
          )}
        </div>

        {error && <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>}
          
        {signupSuccess && <Alert className="bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">
              Account created successfully! Please check your email to confirm your account.
              If you don't see the email, check your spam folder.
            </AlertDescription>
          </Alert>}

        {/* Show referral applied message if a code is present */}
        {referralCode && !error && !signupSuccess && (
          <Alert className="bg-green-50 border-green-200">
            <AlertDescription className="text-green-800 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Referral code applied! You'll receive 30% off your subscription.
            </AlertDescription>
          </Alert>
        )}

        {!signupSuccess ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4 rounded-md">
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleEmailChange}
                  className={`mt-1 ${emailError ? 'border-red-500 focus:border-red-500' : ''}`}
                  placeholder="Enter your email"
                />
                {emailError && (
                  <p className="mt-1 text-sm text-red-600">{emailError}</p>
                )}
              </div>
              <div>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  className="mt-1"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  className={`mt-1 ${passwordError ? 'border-red-500 focus:border-red-500' : ''}`}
                  placeholder="Enter your password"
                />
                {passwordError && (
                  <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                )}
                <PasswordStrengthIndicator
                  password={password}
                  minLength={8}
                  showRequirements={true}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Username generation feedback */}
            <UsernameGenerationFeedback
              isGenerating={isGeneratingUsername}
              generatedUsername={usernameResult?.username}
              isOriginal={usernameResult?.isOriginal}
              alternatives={usernameResult?.alternatives}
              className="mt-4"
            />

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <Checkbox 
                  id="marketingConsent" 
                  checked={marketingConsent} 
                  onCheckedChange={(checked: boolean) => setMarketingConsent(checked === true)}
                />
              </div>
              <div className="ml-3 text-sm">
                <Label 
                  htmlFor="marketingConsent" 
                  className="font-normal text-muted-foreground cursor-pointer"
                >
                  Subscribe to our newsletter for training tips, techniques, and updates
                </Label>
              </div>
            </div>

            <div>
              <Button type="submit" className="w-full" disabled={isLoading || isGeneratingUsername}>
                {isGeneratingUsername ? 'Checking username...' : isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </div>
          </form>
        ) : (
          <EmailConfirmationWaiting
            email={email}
            hasNewsletterConsent={marketingConsent}
          />
        )}
      </div>
    </div>;
}
